/**
 * @file helpers for compose header component
 */

import React, { ReactNode } from 'react';
import { Location } from 'react-router-dom';

import { getComposeTitle } from '../../../components/Compose/helpers';
import { SEARCH_PARAM__LABEL_MODE } from '../../../constants/routing/searchParams';
import translate from '../../../i18n/translate';
import { ComposeModeParam, ComposeTypeParam } from '../../../models/pageParams';
import { removeParamsKeysFromUrl } from '../../../routes/helpers/router';
import { getSearchParamLabelMode } from '../../../routes/helpers/searchParams';
import { getLabelModeUrl } from '../../../routes/urls/label';
import { getCloseComposeSectionUrl } from '../../../routes/urls/searchParams/compose';
import { ComposeNodeId, ComposeNodeType } from '../../compose/props';
import { getLabelsHeading } from '../helpers';
import ContentRightAttachments from '../mobileContentRight/attach';
import ContentRightLabel from '../mobileContentRight/label';

/**
 * Get is draft changed
 *
 * @param isDefaultDraftChanged Whether the default draft is changed
 * @param topicId               Id of the selected topic
 * @param topicIdParam          Topic id from params
 *
 * @returns                     Whether the draft has changed
 */
export const getIsDraftChanged = (
  isDefaultDraftChanged: boolean,
  topicId: string | null,
  topicIdParam: string | null,
): boolean => {
  return isDefaultDraftChanged || topicId !== topicIdParam;
};

/**
 * Get the back URL for the labels mode
 *
 * @param location History location object
 * @returns        Back URL for the labels mode
 */
const getLabelsModeBackUrl = (location: Location) => {
  const labelMode = getSearchParamLabelMode(location, 'compose');

  if (labelMode === null) {
    return getCloseComposeSectionUrl(location);
  }

  if (labelMode === 'MODE_VIEW') {
    return removeParamsKeysFromUrl(location, SEARCH_PARAM__LABEL_MODE);
  }

  return getLabelModeUrl(location, 'compose', 'MODE_VIEW');
};

type Callback = (
  location: Location,
  nodeId: ComposeNodeId,
  nodeType: ComposeNodeType,
  composeType: ComposeTypeParam | null,
) => { title: string; contentRight: ReactNode; backUrl: true | string };

export const mapping = new Map<ComposeModeParam | null, Callback>([
  [
    'assign',
    location => ({
      backUrl: getCloseComposeSectionUrl(location),
      contentRight: null,
      title: translate('ASSIGNEES'),
    }),
  ],
  [
    'attach',
    (location, _, nodeType) => ({
      backUrl: getCloseComposeSectionUrl(location),
      contentRight: <ContentRightAttachments nodeType={nodeType} />,
      title: translate('ATTACHMENT__EDIT__PLACEHOLDER'),
    }),
  ],
  [
    'checklist',
    location => ({
      backUrl: getCloseComposeSectionUrl(location),
      contentRight: null,
      title: translate('MESSAGE__HEADING__CHECKLIST'),
    }),
  ],
  [
    'date',
    location => ({
      backUrl: getCloseComposeSectionUrl(location),
      contentRight: null,
      title: translate('GENERAL__DUE_DATE'),
    }),
  ],
  [
    'description',
    (location, nodeId, nodeType, composeType) => ({
      backUrl: getCloseComposeSectionUrl(location),
      contentRight: null,
      title: getComposeTitle(composeType, nodeId, nodeType),
    }),
  ],
  [
    'label',
    location => ({
      backUrl: getLabelsModeBackUrl(location),
      contentRight: <ContentRightLabel where="compose" />,
      title: getLabelsHeading(location, 'compose'),
    }),
  ],
  [
    'offset',
    location => ({
      backUrl: getCloseComposeSectionUrl(location),
      contentRight: null,
      title: translate('MESSAGE__HEADING__DATE_DUE'),
    }),
  ],
  [
    'template',
    location => ({
      backUrl: getCloseComposeSectionUrl(location),
      contentRight: null,
      title: translate('GENERAL__TEMPLATES'),
    }),
  ],
  [
    'title',
    (location, nodeId, nodeType, composeType) => ({
      backUrl: getCloseComposeSectionUrl(location),
      contentRight: null,
      title: getComposeTitle(composeType, nodeId, nodeType),
    }),
  ],
  [
    'topic',
    location => ({
      backUrl: getCloseComposeSectionUrl(location),
      contentRight: null,
      title: translate('GENERAL__TOPICS'),
    }),
  ],
]);
